import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useReleaseCheckItems, useSetItemIgnored } from '../../api/release-check-api'
import {
  CATEGORY_ORDER,
  CheckCategory,
  ItemStatus,
  ReleaseCheckItem,
} from '../../types/ReleaseCheckItem'

const STATUS_ORDER: ItemStatus[] = [
  'Ok',
  'DiscrepanciesFound',
  'TargetMissing',
  'Failed',
  'Pending',
]

const statusColor = (s: ItemStatus): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  switch (s) {
    case 'Ok':
      return 'success'
    case 'DiscrepanciesFound':
      return 'warning'
    case 'TargetMissing':
    case 'Failed':
      return 'error'
    case 'Pending':
    default:
      return 'info'
  }
}

const CategoryNav = ({
  current,
  counts,
  onSelect,
}: {
  current: CheckCategory
  counts: Record<string, number>
  onSelect: (c: CheckCategory) => void
}) => {
  const { t } = useTranslation()
  return (
    <List dense disablePadding>
      {CATEGORY_ORDER.map((cat) => (
        <ListItemButton
          key={cat}
          selected={cat === current}
          onClick={() => onSelect(cat)}
        >
          <ListItemText primary={t(`releaseCheck.category.${cat}`, cat)} />
          <Chip size="small" label={counts[cat] ?? 0} />
        </ListItemButton>
      ))}
    </List>
  )
}

const ItemRow = ({ item }: { item: ReleaseCheckItem }) => {
  const { t } = useTranslation()
  const [setIgnored, isSetting] = useSetItemIgnored()
  return (
    <TableRow hover sx={{ opacity: item.ignored ? 0.55 : 1 }}>
      <TableCell>
        <Typography variant="body2">{item.targetName ?? item.controlName}</Typography>
      </TableCell>
      <TableCell>{item.id}</TableCell>
      <TableCell>
        <Chip
          size="small"
          label={t(`releaseCheck.status.${item.status}`, item.status)}
          color={statusColor(item.status)}
        />
      </TableCell>
      <TableCell align="right">
        <Switch
          checked={item.ignored}
          disabled={isSetting}
          onChange={(_e, checked) =>
            void setIgnored({ category: item.category, id: item.id, ignored: checked })
          }
        />
      </TableCell>
    </TableRow>
  )
}

const ManagePage = () => {
  const { t } = useTranslation()
  const [items, isLoading] = useReleaseCheckItems()
  const [category, setCategory] = useState<CheckCategory>('KeyVault')
  const [textFilter, setTextFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<Set<ItemStatus>>(
    () => new Set(STATUS_ORDER),
  )

  const counts = useMemo(() => {
    const c: Record<string, number> = {}
    for (const it of items) {
      c[it.category] = (c[it.category] ?? 0) + 1
    }
    return c
  }, [items])

  const filtered = useMemo(() => {
    const needle = textFilter.trim().toLowerCase()
    return items.filter((it) => {
      if (it.category !== category) return false
      if (!statusFilter.has(it.status)) return false
      if (!needle) return true
      return (
        it.id.toLowerCase().includes(needle) ||
        it.controlName.toLowerCase().includes(needle) ||
        (it.targetName?.toLowerCase().includes(needle) ?? false)
      )
    })
  }, [items, category, statusFilter, textFilter])

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
        {t('releaseCheck.manage.title', 'Manage items')}
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
          <Paper variant="outlined" sx={{ width: 260, flexShrink: 0 }}>
            <CategoryNav current={category} counts={counts} onSelect={setCategory} />
          </Paper>

          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder={t('releaseCheck.manage.filterPlaceholder', 'Filter…')}
                value={textFilter}
                onChange={(e) => setTextFilter(e.target.value)}
                sx={{ width: 320 }}
              />
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>{t('releaseCheck.column.status', 'Status')}</InputLabel>
                <Select
                  multiple
                  value={Array.from(statusFilter)}
                  onChange={(e) => {
                    const v = e.target.value
                    setStatusFilter(new Set(typeof v === 'string' ? (v.split(',') as ItemStatus[]) : (v)))
                  }}
                  input={<OutlinedInput label={t('releaseCheck.column.status', 'Status')} />}
                  renderValue={(selected) => {
                    const arr = selected
                    if (arr.length === STATUS_ORDER.length)
                      return t('releaseCheck.manage.filter.all', 'All')
                    return arr.map((s) => t(`releaseCheck.status.${s}`, s)).join(', ')
                  }}
                >
                  {STATUS_ORDER.map((s) => (
                    <MenuItem key={s} value={s}>
                      <Checkbox size="small" checked={statusFilter.has(s)} />
                      <ListItemText primary={t(`releaseCheck.status.${s}`, s)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {filtered.length === 0 ? (
              <Alert severity="info">
                {t('releaseCheck.manage.noItems', 'No items match the current filters.')}
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('releaseCheck.column.resource', 'Resource')}</TableCell>
                      <TableCell>{t('releaseCheck.column.id', 'Identifier')}</TableCell>
                      <TableCell>{t('releaseCheck.column.status', 'Status')}</TableCell>
                      <TableCell align="right">
                        {t('releaseCheck.column.ignore', 'Ignore')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((it) => (
                      <ItemRow key={`${it.category}:${it.id}`} item={it} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  )
}

export default ManagePage
